import moment from "moment";
import CommonUtils from "services/common/utils";
import MapUtils from "services/map/utils";
import { DETAILS_API_TYPES } from "../common/enums";

class LiveUtils {
  static getDefaultSelectedLayer = (map, floor) => {
    let selectedLayer = LiveUtils.getSelectedLayerFromConfig(
      map?.areas || [],
      floor
    );

    if (selectedLayer) {
      return {
        ...selectedLayer,
        type: "area",
      };
    }

    selectedLayer = LiveUtils.getSelectedLayerFromConfig(
      map?.points || [],
      floor
    );

    if (selectedLayer) {
      return {
        ...selectedLayer,
        type: "point",
      };
    }

    selectedLayer = LiveUtils.getSelectedLayerFromConfig(
      map?.paths || [],
      floor
    );

    if (selectedLayer) {
      return {
        ...selectedLayer,
        type: "path",
      };
    }

    return undefined;
  };

  static getSelectedLayerFromConfig = (layers = [], floor) =>
    layers.find(
      (layer) =>
        layer?.floor_level === floor &&
        layer?.options?.selected_by_default === true
    );

  static getSelectedLayers = (state) => state?.map?.selectedLayers;

  static getSelectedLayersFromCurrentFloor = (state, currentFloor) => {
    return this.getSelectedLayers(state).filter(
      (layer) => layer?.floor_level === currentFloor
    );
  };

  static getFromToFiltersFromTimestamp = (timestamp) => {
    return {
      from: Math.floor(
        moment(timestamp * 1000)
          .subtract(1, "h")
          .unix()
      ),
      to: Math.floor(moment(timestamp * 1000).unix()),
    };
  };

  static getSelectedLayersFromCurrentFloorLevel = (
    state,
    currentFloorLevel
  ) => {
    const globalSelectedLayers = LiveUtils.getSelectedLayers(state.Live) || [];

    return globalSelectedLayers
      .filter(
        (layer) =>
          currentFloorLevel === undefined ||
          currentFloorLevel === null ||
          layer?.floor_level === currentFloorLevel
      )
      .map((selectedLayer) => selectedLayer?.id);
  };

  static getFilters = (type = "default", state) => {
    const { radius, position } = state.GlobalFilters;
    const { timestamp } = state.Live?.filters;
    const currentFloor = MapUtils.getFloor(state.Map);
    let data = {};
    let filters = {};

    if (
      type === "radius" &&
      currentFloor !== null &&
      currentFloor !== undefined
    ) {
      filters = {
        ...filters,
        area: { ...(filters?.area || {}), floor_level: currentFloor },
      };
    }

    if (type === "areas") {
      const areas = this.getSelectedLayersFromCurrentFloorLevel(
        state,
        currentFloor
      );

      if (areas.length > 0) {
        data = { ...data, areas };
      }
    }

    if (type === "radius") {
      data = { ...data, radius, position };
    }

    return {
      ...data,
      filters,
      timestamp,
    };
  };

  static formatDisplaysFromApi = (displays = [], config) => {
    const displaysToDisplay = displays;

    return displaysToDisplay.map((display) =>
      LiveUtils.formatDisplayFromApi(display, config)
    );
  };

  static formatDisplayFromApi = (display, config = []) => {
    const displayConfig = config.find(
      (config) =>
        (display?.id || display?.display_id) ===
        (config?.display_id || config?.id)
    );

    return {
      ...displayConfig,
      ...display,
      apiType: display?.type,
      type: "display",
      options: {
        ...(display?.options || {}),
        ...(displayConfig?.display || {}),
      },
    };
  };

  static formatAreasFromApi = (areas = [], config) => {
    const areasToDisplay = areas.filter((area) =>
      config.find(
        (areaConfig) => areaConfig?.area_id && areaConfig?.area_id === area?.id
      )
    );
    return areasToDisplay.map((area) =>
      LiveUtils.formatAreaFromApi(area, config)
    );
  };

  static formatAreaFromApi = (area, config = []) => {
    const areaConfig = config.find(
      (config) =>
        (area?.id || area?.area_id) === (config?.area_id || config?.id)
    );

    return {
      ...areaConfig,
      ...area,
      apiType: area?.type,
      type: "area",
      options: {
        ...(area?.options || {}),
        ...(areaConfig?.display || {}),
      },
    };
  };

  static formatPointsFromApi = (points = [], config) => {
    CommonUtils.resetColorFactoryIndex();

    const pointsToDisplay = points.filter((point) =>
      config.find(
        (pointConfig) =>
          pointConfig?.point_id && pointConfig?.point_id === point?.id
      )
    );
    return pointsToDisplay.map((point) =>
      LiveUtils.formatPointFromApi(point, config)
    );
  };

  static formatPointFromApi = (point, config = []) => {
    const pointConfig = config.find(
      (config) =>
        (point?.id || point?.point_id) === (config?.point_id || config?.id)
    );

    return {
      ...pointConfig,
      ...point,
      apiType: point?.type,
      type: "point",
      location: pointConfig?.display?.location || point?.location,
      options: {
        ...(point?.options || {}),
        ...(pointConfig?.display || {}),
        selection_color:
          pointConfig?.display?.selection_color ||
          pointConfig?.options?.selection_color ||
          CommonUtils.getNewColor(),
      },
    };
  };

  static formatPathsFromApi = (paths = [], config) => {
    CommonUtils.resetColorFactoryIndex();

    const pathsToDisplay = paths.filter((path) =>
      config.find(
        (pathConfig) => pathConfig?.path_id && pathConfig?.path_id === path?.id
      )
    );
    return pathsToDisplay.map((path) =>
      LiveUtils.formatPathFromApi(path, config)
    );
  };

  static formatPathFromApi = (path, config = []) => {
    const pathConfig = config.find(
      (pathConfig) =>
        (path?.id || path?.path_id) === (pathConfig?.path_id || pathConfig?.id)
    );

    return {
      ...pathConfig,
      ...path,
      apiType: path?.type,
      type: "path",
      positions: pathConfig?.display?.polylines || path?.polylines,
      options: {
        ...(path?.options || {}),
        ...(pathConfig?.display || {}),
        selection_color:
          pathConfig?.display?.selection_color ||
          pathConfig?.options?.selection_color ||
          CommonUtils.getNewColor(),
      },
    };
  };

  // @type could be "area", "point" or "path"
  static getFlowDetailsFilters = (type, state) => {
    let filters = {
      ...LiveUtils.getFromToFiltersFromTimestamp(
        state?.Live?.filters?.timestamp
      ),
      flow_types: state?.Live?.filters?.flowTypes,
    };

    if (type === "path") {
      filters = {
        ...filters,
        maximum_duration_in_minutes:
          state?.Live?.filters?.flowMaximumDurationInMinutes,
        duration_unit: state?.Live?.filters?.durationDistributionUnit,
      };
    }

    return filters;
  };

  // @type could be "area", "point" or "path"
  static getFlowGeneralFilters = (type, state) => {
    let filters = {
      ...LiveUtils.getFromToFiltersFromTimestamp(
        state?.Live?.filters?.timestamp
      ),
      flow_types: state?.Live?.filters?.flowTypes,
    };

    if (type === "path") {
      filters = {
        ...filters,
        maximum_duration_in_minutes:
          state?.Live?.filters?.flowMaximumDurationInMinutes,
        duration_unit: state?.Live?.filters?.durationDistributionUnit,
      };
    }

    return filters;
  };

  static getPedestrianDetailsFilters = (filters) => {
    let data = {};
    let formattedFilters = {};
    const areaType = ["pedestrian"];
    const areas = filters.areas.map((layer) => layer?.id);

    if (areaType?.length > 0) {
      formattedFilters = {
        area: {
          "type ($IN)": areaType,
        },
      };
    }

    if (areas.length > 0) {
      data = { ...data, areas };
    }

    return {
      ...data,
      filters: formattedFilters,
      timestamp: filters?.timestamp,
    };
  };

  static getParkDetailsFilters = (filters) => {
    let data = {};
    let formattedFilters = {};
    const areaType = filters?.areaType;
    const areas = filters.areas.map((layer) => layer?.id);

    if (areaType?.length > 0) {
      formattedFilters = {
        area: {
          "type ($IN)": areaType,
        },
      };
    }

    if (areas.length > 0) {
      data = { ...data, areas };
    }

    return {
      ...data,
      filters: formattedFilters,
      timestamp: filters?.timestamp,
    };
  };

  static formatLayersInfosFromApi = (layersInfos, config) => {
    return {
      ...layersInfos,
      areas: LiveUtils.formatAreasFromApi(
        layersInfos?.areas || [],
        config?.areas || []
      ),
      points: LiveUtils.formatPointsFromApi(
        layersInfos?.points || [],
        config?.points || []
      ),
      paths: LiveUtils.formatPathsFromApi(
        layersInfos?.paths || [],
        config?.paths || []
      ),
      displays: LiveUtils.formatDisplaysFromApi(
        layersInfos?.displays || [],
        config?.displays || []
      ),
    };
  };

  static getLayersFromCurrentFloor = (state, currentFloorLevel, type) => {
    const currentFloor =
      currentFloorLevel !== null && currentFloorLevel !== undefined
        ? currentFloorLevel
        : MapUtils.getFloor(state.Map);
    const layers = state.Live?.map?.[type] || [];

    return layers
      .filter((layer) => layer?.type !== "UNEQUIPPED")
      .filter(
        (layer) =>
          currentFloor === undefined ||
          currentFloor === null ||
          layer?.floor_level === currentFloor
      );
  };

  static formatLayersFromCurrentFloor = (state) => {
    const currentFloor = MapUtils.getFloor(state.Map);
    const selectedLayersFromCurrentFloor =
      this.getSelectedLayersFromCurrentFloorLevel(state, currentFloor);
    const currentFloorAreas = this.getLayersFromCurrentFloor(
      state,
      currentFloor,
      "areas"
    );
    const currentFloorPaths = this.getLayersFromCurrentFloor(
      state,
      currentFloor,
      "paths"
    );
    const currentFloorPoints = this.getLayersFromCurrentFloor(
      state,
      currentFloor,
      "points"
    );

    return {
      areas: currentFloorAreas?.map((area) => ({
        ...area,
        selected: selectedLayersFromCurrentFloor?.indexOf(area?.id) >= 0,
      })),
      paths: currentFloorPaths?.map((path) => ({
        ...path,
        selected: selectedLayersFromCurrentFloor?.indexOf(path?.id) >= 0,
      })),
      points: currentFloorPoints?.map((point) => ({
        ...point,
        selected: selectedLayersFromCurrentFloor?.indexOf(point?.id) >= 0,
      })),
    };
  };

  static getParkedCarsFilters = (type = "default", state) => {
    const { include_is_unavailable } = state.GlobalFilters;
    const { areaType, timestamp } = state.Live?.filters;
    const selectedLayers = LiveUtils.getSelectedLayers(state.Live) || [];
    const floorLevel = MapUtils.getFloor(state.Map);
    let data = {};
    let filters = {};

    if (areaType?.length > 0) {
      filters = {
        area: {
          "type ($IN)": areaType,
        },
      };
    }

    if (type === "areas") {
      const allowedDetailsApiTypes = [
        DETAILS_API_TYPES.PARK,
        DETAILS_API_TYPES.PARK_AND_FLOW,
      ];
      const areas = selectedLayers
        .filter(
          (layer) =>
            floorLevel === undefined ||
            floorLevel === null ||
            layer?.floor_level === floorLevel
        )
        .filter(
          (layer) =>
            layer.type === "area" &&
            allowedDetailsApiTypes.includes(layer?.apiType)
        )
        .map((selectedLayer) => selectedLayer?.id);

      if (areas.length > 0) {
        data = { ...data, areas };
      }
    }

    return {
      ...data,
      filters,
      timestamp,
      include_is_unavailable,
    };
  };
}

export default LiveUtils;
